import React, { useState } from 'react'
import styled from 'styled-components'
import BigNumber from 'bignumber.js'
import { Flex, Box, Text, ExpandableLabel, LinkExternal, Grid } from 'vvs-uikit'
import { VVS_PER_YEAR } from 'config'
import { useTranslation } from 'contexts/Localization'
import { getApy } from 'utils/compoundApyHelpers'
import { getTokenRewardApr } from 'utils/getFarmRewards'
import { formatNumber } from 'utils/formatBalance'

const Footer = styled(Flex)`
  width: 100%;
  background: ${({ theme }) => theme.colors.dropdown};
`

const BulletList = styled.ul`
  list-style-type: none;
  margin-top: 16px;
  padding: 0;
  li {
    margin: 0;
    padding: 0;
  }
  li::before {
    content: '•';
    margin-right: 4px;
    color: ${({ theme }) => theme.colors.textSubtle};
  }
  li::marker {
    font-size: 12px;
  }
`

interface RoiCalculatorFooterProps {
  isFarm: boolean
  apr: number
  displayApr: string
  autoCompoundFrequency: number
  linkLabel: string
  linkHref: string
  performanceFee: number
  vvsPrice: number
  poolWeight?: BigNumber
  poolLiquidity?: BigNumber
  rewardData?: any[]
  farms?: any[]
}

const RoiCalculatorFooter: React.FC<RoiCalculatorFooterProps> = ({
  isFarm,
  apr,
  displayApr,
  autoCompoundFrequency,
  linkLabel,
  linkHref,
  performanceFee,
  vvsPrice,
  poolWeight,
  poolLiquidity,
  rewardData,
  farms,
}) => {
  const [isExpanded, setIsExpanded] = useState(false)
  const { t } = useTranslation()

  const gridRowCount = isFarm ? 4 : 2
  const apy = (getApy(apr, autoCompoundFrequency > 0 ? autoCompoundFrequency : 1, 365, performanceFee) * 100).toFixed(2)
  let getVVSAprOnly = false
  let vvsApr = null
  const now = Date.now() / 1000
  if (rewardData) {
    rewardData.forEach((data) => {
      if (
        parseInt(data.rewardStartTimestamp) <= now &&
        now <= parseInt(data.rewardEndTimestamp) &&
        data.allocPoint !== '0'
      ) {
        getVVSAprOnly = true
      }
    })
    if (getVVSAprOnly) {
      const yearlyVvsRewardAllocation: BigNumber = poolWeight ? poolWeight.times(VVS_PER_YEAR) : new BigNumber(NaN)
      const vvsRewardsApr: BigNumber = yearlyVvsRewardAllocation.times(vvsPrice).div(poolLiquidity).times(100)
      let vvsRewardsAprAsNumber = null
      if (!vvsRewardsApr.isNaN() && vvsRewardsApr.isFinite()) {
        vvsRewardsAprAsNumber = vvsRewardsApr.toNumber()
      }
      vvsApr = vvsRewardsAprAsNumber
    }
  }

  return (
    <Footer p="16px" flexDirection="column">
      <ExpandableLabel expanded={isExpanded} onClick={() => setIsExpanded((prev) => !prev)}>
        {isExpanded ? t('Hide') : t('Details')}
      </ExpandableLabel>
      {isExpanded && (
        <Box px="8px">
          <Grid gridTemplateColumns="2.5fr 1fr" gridRowGap="8px" gridTemplateRows={`repeat(${gridRowCount}, auto)`}>
            {isFarm && (
              <>
                <Text color="textSubtle" small>
                  {t('APR Cumulative (incl. LP rewards)')}
                </Text>
                <Text small textAlign="right">
                  {displayApr}%
                </Text>
              </>
            )}
            <Text color="textSubtle" small>
              {isFarm ? t('Base APR (VVS yield only)') : t('APR')}
            </Text>
            <Text small textAlign="right">
              {isFarm && getVVSAprOnly ? `${formatNumber(vvsApr)}%` : `${formatNumber(apr)}%`}
            </Text>
            {rewardData
              ? rewardData.map((data) => {
                  const tokenApr = getTokenRewardApr(
                    data.token.address,
                    data.token.decimals,
                    data.totalRewardPerSecond,
                    data.rewardStartTimestamp,
                    data.rewardEndTimestamp,
                    data.allocPoint,
                    data.totalAllocPoint,
                    poolLiquidity,
                    farms,
                  )
                  return (
                    <React.Fragment key={data.token.address}>
                      <Flex key={data.token.address}>
                        <Text color="textSubtle" small>
                          {t(`Base APR (${data.token.symbol} yield only)`)}
                        </Text>
                      </Flex>
                      <Text small textAlign="right">
                        {tokenApr.toFixed(2)}%
                      </Text>
                    </React.Fragment>
                  )
                })
              : null}
            <Text color="textSubtle" small>
              {t('APY (%compoundTimes%x daily compound)', {
                compoundTimes: autoCompoundFrequency > 0 ? autoCompoundFrequency : 1,
              })}
            </Text>
            <Text small textAlign="right">
              {apy}%
            </Text>
          </Grid>
          <BulletList>
            <li>
              <Text fontSize="12px" textAlign="center" color="textSubtle" display="inline">
                {t('Calculated based on current rates.')}
              </Text>
            </li>
            {isFarm && (
              <li>
                <Text fontSize="12px" textAlign="center" color="textSubtle" display="inline">
                  {t('LP rewards: 0.20% trading fees, distributed proportionally among LP token holders.')}
                </Text>
              </li>
            )}
            <li>
              <Text fontSize="12px" textAlign="center" color="textSubtle" display="inline">
                {t(
                  'All figures are estimates provided for your convenience only, and by no means represent guaranteed returns.',
                )}
              </Text>
            </li>
            {performanceFee > 0 && (
              <li>
                <Text mt="14px" fontSize="12px" textAlign="center" color="textSubtle" display="inline">
                  {t('All estimated rates take into account this pool’s %fee%% performance fee', {
                    fee: performanceFee,
                  })}
                </Text>
              </li>
            )}
          </BulletList>
          <Flex justifyContent="center" mt="24px">
            <LinkExternal href={linkHref}>{linkLabel}</LinkExternal>
          </Flex>
        </Box>
      )}
    </Footer>
  )
}

export default RoiCalculatorFooter
